import { clientbookApi } from '../core/clientbook-api';

// ============================================
// MERCHANT TAGS
// ============================================

/**
 * Get merchant-level tags (for audience targeting)
 * @param {Object} options
 * @param {Object} options.query - Query params (offset, limit, archived)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getMerchantTags = async ({ query = {}, loadingStatusOff = false }) => {
  const defaultQuery = { offset: 0, limit: 500, ...query };
  return clientbookApi.get({
    url: '/merchants/tags',
    query: defaultQuery,
    loading: !loadingStatusOff,
  });
};

// ============================================
// EVENTS
// ============================================

/**
 * Get list of events (paginated)
 * @param {Object} options
 * @param {Object} options.query - Query params (page, limit, etc.)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getEvents = async ({ query = {}, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: '/merchants/events',
    query,
    loading: !loadingStatusOff,
  });
};

/**
 * Create a new event (requires at least 1 session)
 * @param {Object} options
 * @param {Object} options.data - Event data with sessions
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const createEvent = async ({ data, loadingStatusOff = false }) => {
  return clientbookApi.post({
    url: '/merchants/events',
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Get single event by ID
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getEvent = async ({ eventId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Update an existing event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.data - Updated event data
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const updateEvent = async ({ eventId, data, loadingStatusOff = false }) => {
  return clientbookApi.patch({
    url: `/merchants/events/${eventId}`,
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Archive (soft delete) an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const archiveEvent = async ({ eventId, loadingStatusOff = false }) => {
  return clientbookApi.delete({
    url: `/merchants/events/${eventId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Get computed audience for an event (paginated)
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.query - Query params (page, limit, etc.)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getEventAudience = async ({ eventId, query = {}, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}/audience`,
    query,
    loading: !loadingStatusOff,
  });
};

// ============================================
// SESSIONS
// ============================================

/**
 * Get all sessions for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getEventSessions = async ({ eventId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}/sessions`,
    loading: !loadingStatusOff,
  });
};

/**
 * Create a new session for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {Object} options.data - Session data (date, startTime, endTime)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const createEventSession = async ({ eventId, data, loadingStatusOff = false }) => {
  return clientbookApi.post({
    url: `/merchants/events/${eventId}/sessions`,
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Get single session by ID
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getSession = async ({ eventId, sessionId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}/sessions/${sessionId}`,
    loading: !loadingStatusOff,
  });
};

/**
 * Update an existing session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {Object} options.data - Updated session data
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const updateSession = async ({ eventId, sessionId, data, loadingStatusOff = false }) => {
  return clientbookApi.patch({
    url: `/merchants/events/${eventId}/sessions/${sessionId}`,
    data,
    loading: !loadingStatusOff,
  });
};

/**
 * Archive (soft delete) a session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const archiveSession = async ({ eventId, sessionId, loadingStatusOff = false }) => {
  return clientbookApi.delete({
    url: `/merchants/events/${eventId}/sessions/${sessionId}`,
    loading: !loadingStatusOff,
  });
};

// ============================================
// SESSION STORES
// ============================================

/**
 * Get all stores for a session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getSessionStores = async ({ eventId, sessionId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}/sessions/${sessionId}/stores`,
    loading: !loadingStatusOff,
  });
};

/**
 * Replace all stores for a session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {Array<number>} options.storeIds - Array of store IDs
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const replaceSessionStores = async ({ eventId, sessionId, storeIds, loadingStatusOff = false }) => {
  return clientbookApi.put({
    url: `/merchants/events/${eventId}/sessions/${sessionId}/stores`,
    data: { storeIds },
    loading: !loadingStatusOff,
  });
};

/**
 * Add stores to a session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {Array<number>} options.storeIds - Array of store IDs to add
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const addSessionStores = async ({ eventId, sessionId, storeIds, loadingStatusOff = false }) => {
  return clientbookApi.post({
    url: `/merchants/events/${eventId}/sessions/${sessionId}/stores`,
    data: { storeIds },
    loading: !loadingStatusOff,
  });
};

/**
 * Remove a store from a session
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {string|number} options.sessionId - Session ID
 * @param {string|number} options.storeId - Store ID to remove
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const removeSessionStore = async ({ eventId, sessionId, storeId, loadingStatusOff = false }) => {
  return clientbookApi.delete({
    url: `/merchants/events/${eventId}/sessions/${sessionId}/stores/${storeId}`,
    loading: !loadingStatusOff,
  });
};

// ============================================
// CLIENT DATA FOR EXPANDED ROWS
// ============================================

/**
 * Get the last outbound message sent to a client
 * @param {Object} options
 * @param {string|number} options.clientId - Client ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getClientLastMessage = async ({ clientId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/clients/${clientId}/messages`,
    query: {
      offset: 0,
      limit: 1,
      sort: '-sentDateTime',
      direction: 'outbound',
    },
    loading: !loadingStatusOff,
  });
};

/**
 * Get client activities from a specific date
 * @param {Object} options
 * @param {string|number} options.clientId - Client ID
 * @param {string} options.fromDate - ISO 8601 date to filter from
 * @param {number} options.limit - Number of activities to fetch (default: 5)
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getClientActivitiesFromDate = async ({ clientId, fromDate, limit = 5, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/clients/${clientId}/activities`,
    query: {
      offset: 0,
      limit,
      sort: '-createdDate',
      fromDate,
    },
    loading: !loadingStatusOff,
  });
};

/**
 * Get associate by ID
 * @param {Object} options
 * @param {string|number} options.associateId - Associate ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getAssociateById = async ({ associateId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/associates/${associateId}`,
    loading: !loadingStatusOff,
  });
};

// ============================================
// SALES ATTRIBUTION
// ============================================

/**
 * Get sales attribution for an event
 * @param {Object} options
 * @param {string|number} options.eventId - Event ID
 * @param {boolean} options.loadingStatusOff - Disable loading indicator
 */
export const getEventSalesAttribution = async ({ eventId, loadingStatusOff = false }) => {
  return clientbookApi.get({
    url: `/merchants/events/${eventId}/sales-attribution`,
    loading: !loadingStatusOff,
  });
};
